<!-- Собирает форму создания и редактирования трекера телематики -->
<template>
  <div class="inline-tracker-form">
    <div class="inline-form-grid">
      <div class="inline-form-group">
        <base-label>Название</base-label>
        <base-input
          v-model="form.name"
          placeholder="Название трекера"
        />
      </div>

      <div class="inline-form-group">
        <base-label required>IMEI</base-label>
        <base-input
          v-model="form.imei"
          placeholder="123456789012345"
          :invalid="errors.imei"
          @update:modelValue="errors.imei = false"
        />
        <span v-if="errors.imei" class="error-message">
          Это поле обязательно
        </span>
      </div>

      <div class="inline-form-group">
        <base-label required>Протокол</base-label>
        <base-select
          v-model="form.protocol"
          :options="protocolOptions"
          :searchable="false"
          :invalid="errors.protocol"
          placeholder="Выберите протокол"
          @change="errors.protocol = false"
        />
        <span v-if="errors.protocol" class="error-message">
          Это поле обязательно
        </span>
      </div>

      <div class="inline-form-group">
        <base-label>Номер SIM</base-label>
        <base-input v-model="form.sim_phone" placeholder="+7..." />
      </div>

      <div class="inline-form-group">
        <base-label required>Транспорт</base-label>

        <VehicleSelect
          v-if="!isEditing"
          v-model="form.vehicle_id"
          :invalid="errors.vehicle_id"
          :with-button="true"
          button-label="Создать транспорт"
          @change="errors.vehicle_id = false"
          @button-click="openVehicleModal"
        />

        <div v-else class="vehicle-readonly">
          <slot name="vehicle-display">
            <span class="vehicle-readonly-value">
              {{ form.vehicle_id ? `#${form.vehicle_id}` : "—" }}
            </span>
          </slot>
        </div>

        <span v-if="errors.vehicle_id" class="error-message">
          Это поле обязательно
        </span>
      </div>

      <div class="inline-form-group">
        <base-label>Пароль протокола</base-label>
        <base-input
          v-model="form.protocol_password"
          :is-password="true"
          :show-password-toggle="false"
          :clearable="false"
          :editable="
            isEditing && !!initialData.protocol_password_present
          "
          editable-placeholder="Пароль задан"
          placeholder="Введите пароль протокола"
        />
      </div>

    </div>

    <TrackerProtocolInfo :protocol="selectedProtocol" />

    <div class="inline-form-group skip-protocol-password">
      <base-checkbox
        v-model="form.skip_protocol_password"
        label="Принимать пакеты без проверки пароля"
        @change="handleSkipPasswordChange"
      />
      <div
        class="skip-warning"
        :class="{
          'skip-warning--active': form.skip_protocol_password,
        }"
      >
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="skip-warning-icon"
        >
          <path
            d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
          />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
        <span v-if="form.skip_protocol_password">
          Включено — сервер будет принимать любые пакеты с IMEI этого
          трекера, игнорируя пароль. Это может привести к подмене
          данных от стороннего устройства с тем же IMEI.
        </span>
        <span v-else>
          При включении этой опции проверка пароля протокола будет
          отключена. Используйте только если пароль утерян и вы
          осознаёте риски.
        </span>
      </div>
    </div>

    <div class="inline-form-actions">
      <base-primary-button
        @click="save"
        :loading="isLoading"
        class="btn-sm"
      >
        {{ isEditing ? "Сохранить" : "Добавить" }}
      </base-primary-button>
      <base-button @click="$emit('cancel')" class="btn-sm">
        Отмена
      </base-button>
    </div>

    <base-dialog
      :visible="showSkipConfirmDialog"
      title="Отключить проверку пароля?"
      :actions="skipDialogActions"
      :close-on-overlay="false"
      @action="handleSkipDialogAction"
    >
      <p class="dialog-warning-text">
        <strong>Внимание!</strong>
        После включения этой опции сервер будет принимать любые пакеты
        от устройств с IMEI
        <strong>{{ form.imei || "этого трекера" }}</strong>
        , не проверяя пароль протокола.
      </p>
      <p class="dialog-warning-text dialog-warning-text--mt">
        Это означает, что стороннее устройство с таким же IMEI сможет
        отправлять данные от имени вашего трекера. Продолжить?
      </p>
    </base-dialog>

    <!-- Модальное окно создания транспорта -->
    <AddVehicleModal
      v-if="isVehicleModalVisible"
      @vehicle-created="handleVehicleCreated"
      @close="isVehicleModalVisible = false"
    />
  </div>
</template>

<script>
import { useNotificationStore } from "@/stores/notificationStore";

import VehicleSelect from "@/components/Vehicle/VehicleSelect.vue";
import AddVehicleModal from "@/components/Vehicle/AddVehicleModal.vue";
import TrackerProtocolInfo from "@/components/Telemetry/TrackerProtocolInfo.vue";
import {
  TELEMETRY_PROTOCOLS,
  findTelemetryProtocol,
} from "@/constants/telemetryProtocols";

export default {
  name: "TelemetryTrackerForm",
  components: {
    AddVehicleModal,
    TrackerProtocolInfo,
    VehicleSelect,
  },
  props: {
    initialData: { type: Object, default: null },
    isLoading: { type: Boolean, default: false },
  },
  emits: ["save", "cancel"],
  data() {
    return {
      form: {
        name: "",
        imei: "",
        sim_phone: "",
        protocol: null,
        vehicle_id: null,
        protocol_password: "",
        skip_protocol_password: false,
      },
      errors: {
        imei: false,
        protocol: false,
        vehicle_id: false,
      },
      showSkipConfirmDialog: false,
      isVehicleModalVisible: false,
      skipDialogActions: [
        {
          key: "confirm",
          label: "Да, отключить проверку",
          type: "danger",
        },
        {
          key: "close",
          label: "Отмена",
          type: "secondary",
        },
      ],
    };
  },
  computed: {
    notificationStore() {
      return useNotificationStore();
    },
    isEditing() {
      return !!this.initialData;
    },
    protocolOptions() {
      return TELEMETRY_PROTOCOLS;
    },
    selectedProtocol() {
      return findTelemetryProtocol(this.form.protocol);
    },
  },
  watch: {
    initialData: {
      immediate: true,
      handler(val) {
        this.resetErrors();
        this.form = val
          ? {
              name: val.name || "",
              imei: val.imei || "",
              sim_phone: val.sim_phone || "",
              protocol: val.protocol || null,
              vehicle_id: val.vehicle_id || null,
              protocol_password: "",
              skip_protocol_password:
                val.skip_protocol_password || false,
            }
          : {
              name: "",
              imei: "",
              sim_phone: "",
              protocol: null,
              vehicle_id: null,
              protocol_password: "",
              skip_protocol_password: false,
            };
      },
    },
  },
  methods: {
    resetErrors() {
      this.errors = {
        imei: false,
        protocol: false,
        vehicle_id: false,
      };
    },

    handleSkipPasswordChange(newValue) {
      if (newValue) {
        this.form.skip_protocol_password = false;
        this.showSkipConfirmDialog = true;
      }
    },

    handleSkipDialogAction(key) {
      this.showSkipConfirmDialog = false;
      if (key === "confirm") {
        this.form.skip_protocol_password = true;
      }
    },

    openVehicleModal() {
      this.isVehicleModalVisible = true;
    },

    handleVehicleCreated(newVehicle) {
      this.form.vehicle_id = newVehicle.id;
      this.errors.vehicle_id = false;
      this.isVehicleModalVisible = false;
    },

    validate() {
      this.resetErrors();
      let isValid = true;

      if (!String(this.form.imei || "").trim()) {
        this.errors.imei = true;
        isValid = false;
      }
      if (!this.form.protocol) {
        this.errors.protocol = true;
        isValid = false;
      }
      if (!this.form.vehicle_id) {
        this.errors.vehicle_id = true;
        isValid = false;
      }

      if (!isValid) {
        this.notificationStore.showError(
          "Пожалуйста, заполните все обязательные поля"
        );
      }

      return isValid;
    },

    save() {
      if (!this.validate()) {
        this.$nextTick(() => {
          const firstError = this.$el.querySelector(
            ".invalid, .error-message"
          );
          if (firstError) {
            const group = firstError.closest(".inline-form-group");
            (group || firstError).scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        });
        return;
      }

      const payload = {
        name: this.form.name,
        imei: this.form.imei,
        sim_phone: this.form.sim_phone,
        protocol: this.form.protocol,
        vehicle_id: this.form.vehicle_id,
        skip_protocol_password: this.form.skip_protocol_password,
      };

      if (this.form.protocol_password?.trim()) {
        payload.protocol_password =
          this.form.protocol_password.trim();
      }

      this.$emit("save", payload);
    },
  },
};
</script>

<style scoped>
.inline-form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: var(--base-in-indent);
}

.inline-form-group {
  display: flex;
  flex-direction: column;
}

.skip-protocol-password {
  margin-bottom: var(--base-in-indent);
}

.inline-form-actions {
  display: flex;
  gap: 8px;
}

.btn-sm {
  padding: 6px 14px;
  font-size: 13px;
}

.error-message {
  margin-top: 5px;
  color: var(--color-danger);
  font-size: 12px;
}

.vehicle-readonly {
  display: flex;
  align-items: center;
  min-height: 28px;
  padding: 1px 0;
}

.vehicle-readonly-value {
  font-size: 14px;
  color: #6b7280;
}

.skip-warning {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  margin-top: 8px;
  padding: 10px 12px;
  border-radius: 6px;
  background-color: #fefce8;
  border: 1px solid #fde68a;
  font-size: 13px;
  color: #92400e;
  line-height: 1.5;
}

.skip-warning--active {
  background-color: #fff7ed;
  border-color: #fdba74;
  color: #9a3412;
}

.skip-warning-icon {
  flex-shrink: 0;
  margin-top: 1px;
  color: #d97706;
}

.skip-warning--active .skip-warning-icon {
  color: #ea580c;
}

.dialog-warning-text {
  font-size: 14px;
  color: #4b5563;
  line-height: 1.6;
  max-width: none;
}

.dialog-warning-text--mt {
  margin-top: 10px;
}

@media (max-width: 600px) {
  .inline-form-grid {
    grid-template-columns: 1fr;
  }
}
</style>
